import { request } from './client'
import type { Escalation } from './escalations'
import type { SupervisorLane } from './supervisorDay'

/* **W-10 — THE OPERATIONS DASHBOARD.** Sprint 61.

   Pack 1, unusually directly: *"its job is not to show everything — it is to answer one
   question in the first five seconds: can we cover today?"* — and *"the headline is a gap,
   not a total."*

   Almost nothing here is new. Readiness comes from the same rule the room board uses, the
   lanes from the same reader the supervisor's day uses, the worklist from the same query
   the handset reads. That is the point: a dashboard with a second opinion about the word
   "ready" is the one screen on which a manager would believe the wrong number. */

/**
 * **THE HEADLINE IS A SENTENCE, AND THIS SAYS WHICH SENTENCE.**
 *
 * Four genuinely different situations with four different next actions. A single figure
 * with a colour would have invented the threshold between them, and `CANNOT_BE_COVERED`
 * is the one a manager has to act on outside this product entirely.
 */
export type CoverageStanding =
  | 'NO_ARRIVALS' | 'COVERED' | 'SHORTFALL' | 'CANNOT_BE_COVERED'

/** One row of the "not ready, by reason" panel — a count of rooms, not the reason itself,
 *  which `roomState` already owns under the name `NotReadyReason`. */
export interface NotReadyCount {
  reason:
    | 'AWAITING_CLEAN' | 'BEING_CLEANED' | 'AWAITING_INSPECTION'
    | 'OUT_OF_SERVICE' | 'OUT_OF_ORDER' | 'STILL_OCCUPIED'
  rooms: number
}

export interface OperationsView {
  propertyId: string
  generatedAtUtc: string
  localDate: string

  standing: CoverageStanding
  arrivals: number
  readyNow: number
  /** How many more rooms must become ready. Never negative. */
  shortfall: number
  /** How many could still become ready today — dirty, being cleaned, or awaiting
   *  inspection. **Not** the ones out of service: those need a decision, not a push. */
  recoverable: number
  /** The earliest arrival with no ready room waiting, or null. **Never an invented
   *  check-in hour** — see the server's note. */
  nextArrivalUtc: string | null

  totalRooms: number
  departuresDone: number
  departuresExpected: number
  openTasks: number
  runningTasks: number
  /** Rooms submitted and waiting for somebody to inspect them. See the change note: a task
   *  in this product has no due time, so this tile counts a queue caused by a person. */
  awaitingInspection: number
  /** Minutes the oldest has waited. **Null, not zero**, when nothing is waiting. */
  oldestWaitingMinutes: number | null
  openFaults: number
  blockingFaults: number

  notReady: NotReadyCount[]
  lanes: SupervisorLane[]
  decisions: Escalation[]
}

export const fetchOperations = (
  propertyId: string,
  options: { signal?: AbortSignal } = {},
) => request<OperationsView>(
  `/properties/${propertyId}/operations`,
  options.signal ? { signal: options.signal } : {},
)
